import React, { useEffect } from "react";
import {
  Activity,
  ClipboardList,
  UserCheck,
  BadgeCheck,
  Hammer,
  MapPin,
  Calendar,
  Phone,
  Mail,
  Building,
} from "lucide-react";
import { useGetFieldForceActivityLogs } from "@features/wx/fieldForce/hooks";
import { useNavigate, useParams } from "react-router-dom";

export const ActivityTab = () => {
  const { id } = useParams();
  const { data: activityLogs, refetch } = useGetFieldForceActivityLogs(id);
  const navigate = useNavigate();
  useEffect(() => {
    refetch(); // Just refetch the current query
  }, [location.pathname]);

  const getActivityIcon = (title) => {
    if (title.includes("service order") || title.includes("order")) {
      return { icon: ClipboardList, color: "text-amber-500 bg-amber-50" };
    }
    if (
      title.includes("information update") ||
      title.includes("profile") ||
      title.includes("personal")
    ) {
      return { icon: UserCheck, color: "text-blue-500 bg-blue-50" };
    }
    if (title.includes("certification") || title.includes("certificate")) {
      return { icon: BadgeCheck, color: "text-green-500 bg-green-50" };
    }
    if (title.includes("training")) {
      return { icon: Hammer, color: "text-purple-500 bg-purple-50" };
    }
    if (title.includes("assignment") || title.includes("area")) {
      return { icon: MapPin, color: "text-red-500 bg-red-50" };
    }
    return { icon: Activity, color: "text-gray-500 bg-gray-50" };
  };

  const formatAreaPath = (areaArray) => {
    if (!areaArray || !Array.isArray(areaArray) || areaArray.length === 0)
      return "";
    return areaArray[0]
      .split("#")
      .map((part) => part.split("_")[0])
      .join(" → ");
  };

  const formatSchedule = (schedule) => {
    if (!schedule || !Array.isArray(schedule)) return { working: "", off: "" };

    const workingDays = schedule.filter((day) => !day.isWeeklyOff);
    const offDays = schedule.filter((day) => day.isWeeklyOff);

    return {
      working: workingDays.map((day) => day.day).join(", "),
      off: offDays.map((day) => day.day).join(", "),
    };
  };

  return (
    <div className="">
      <h3 className="card-heading-text mb-4 flex items-center">
        <Activity className="mr-2 h-5 w-5 text-blue-500" />
        Recent Activity
      </h3>

      {activityLogs?.length > 0 ? (
        <div className="relative pl-8 space-y-8 before:absolute before:inset-y-0 before:left-4 before:w-px before:bg-slate-200">
          {activityLogs.map((log, index) => {
            const { icon: ActivityIcon, color: iconColor } = getActivityIcon(
              log.title
            );
            const scheduleInfo = log.data?.additionalData?.availabilitySchedule
              ? formatSchedule(log.data.additionalData.availabilitySchedule)
              : null;

            return (
              <div
                key={`${log.date}-${log.time}-${index}`}
                className="relative"
              >
                <div
                  className={`absolute -left-8 mt-1.5 flex h-8 w-8 items-center justify-center rounded-full border border-white ${iconColor}`}
                >
                  <ActivityIcon className="h-4 w-4" />
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                  {/* Header */}
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h4 className="font-medium text-gray-900 capitalize">
                        {log.title}
                      </h4>
                      <p className="text-sm text-gray-600 mt-1">
                        {log.description}
                      </p>
                    </div>
                    <div className="text-right text-xs text-gray-500">
                      <div className="flex items-center">
                        <Calendar className="h-3 w-3 mr-1" />
                        {log.date} at {log.time}
                      </div>
                    </div>
                  </div>

                  {/* Updated by */}
                  {log.user && (
                    <div className="mb-3 p-2 bg-gray-50 rounded border-l-4 border-blue-400">
                      <p className="text-xs text-gray-600">Updated by:</p>
                      <div className="flex items-center text-sm">
                        <UserCheck className="h-3 w-3 mr-1 text-blue-500" />
                        <span className="font-medium">{log.user.name}</span>
                        <span className="mx-2 text-gray-400">•</span>
                        <span className="text-gray-600">{log.user.email}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center p-8 bg-gray-50 rounded-lg border border-dashed">
          <Activity className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h4 className="text-lg font-medium mb-2">No Activity Logs</h4>
          <p className="text-gray-600">
            No activity has been recorded for this technician yet.
          </p>
        </div>
      )}
    </div>
  );
};
